// INFO: Prevents vite from tree shaking the file given that it has no export being consumed
import '@/store/chat/chat.listener';

import { combineReducers, configureStore } from '@reduxjs/toolkit';

import langReducer from '@/configuration/lang/langSlice';
import languageMiddleware from '@/configuration/lang/languageMiddleware';
import loginReducer from '@/configuration/login/loginSlice';
import configReducer from '@/configuration/setup/configSlice';
import tokenReducer from '@/configuration/tokenHandling/tokenSlice';
import { api } from '@/services/config/config';
import { cookieApi } from '@/services/cookieApi/cookieApi';
import { userApi } from '@/services/userApi/userApi';
import { listenerMiddleware } from '@/store/middleware/listenerMiddleware';
import { APP_NAME } from '@/utils/constants';

import aboutReducer from './about/aboutSlice';
import appReducer from './app/appSlice';
import chatReducer from './chat/chat.slice';
import kpiDataVisualizationPreferenceReducer from './chat/KPIDataVisualizationPreference/KPIDataVisualizationPreference.slice'
import dialogReducer from './dialog/dialogSlice';
import faqReducer from './faq/faqSlice';
import feedbackReducer from './feedback/feedbackSlice';
import layoutReducer from './layout/layoutSlice';
import onboardingReducer from './onboarding/onboardingSlice';
import processingStatusReducer from './processingStatus/processingStatusSlice';
import searchReducer from './search/searchSlice';
import sidebarReducer from './sidebar/sidebarSlice';
import threadReducer from './thread/threadSlice';
import tipsReducer from './tips/tipsSlice';
import userReducer from './user/userSlice';

export const rootReducer = combineReducers({
  [api.reducerPath]: api.reducer,
  [userApi.reducerPath]: userApi.reducer,
  [cookieApi.reducerPath]: cookieApi.reducer,
  config: configReducer,
  lang: langReducer,
  app: appReducer,
  user: userReducer,
  chat: chatReducer,
  login: loginReducer,
  tokenHandling: tokenReducer,
  feedback: feedbackReducer,
  thread: threadReducer,
  dialog: dialogReducer,
  onboarding: onboardingReducer,
  sidebar: sidebarReducer,
  faq: faqReducer,
  about: aboutReducer,
  layout: layoutReducer,
  tips: tipsReducer,
  search: searchReducer,
  processingStatus: processingStatusReducer,
  kpiDataVisualizationPreference: kpiDataVisualizationPreferenceReducer,
});

/**
 * In RTK 2.x, `GetDefaultMiddleware` is no longer a public export.
 * The parameter is typed loosely here because configureStore infers the correct
 * type internally when this callback is passed to the 'middleware' option.
 * @see https://redux-toolkit.js.org/usage/migrating-rtk-2
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const rootMiddleware = (getDefaultMiddleware: () => any) =>
  getDefaultMiddleware()
  .prepend(listenerMiddleware.middleware)
  .concat(
    api.middleware,
    userApi.middleware,
    cookieApi.middleware,
    languageMiddleware,
  );

const devToolsConfig = {
  name: APP_NAME,
  trace: true,
};

export const storeInit = (preloadedState?: Partial<RootState>) =>
  configureStore({
    reducer: rootReducer,
    middleware: rootMiddleware,
    preloadedState,
    devTools: import.meta.env.DEV ? devToolsConfig : false,
  });


export const reduxStore = storeInit();

export type RootState = ReturnType<typeof rootReducer>;
export type AppStore = ReturnType<typeof storeInit>;
export type RootDispatch = AppStore['dispatch'];
